/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchGaimaResult, pollGaima } from "../../../api";
import { RootState } from "../../store";
import {
  GaimaTask,
  GaimaStatus,
  LearnerType,
  Lesson,
  LessonType,
} from "../../../types";
import { copyAndSet, extractErrorMessageFromError } from "../../../utils";

export interface GaimaState {
  tasks: GaimaTask[];
}

const initialState: GaimaState = {
  tasks: [],
};

/** Actions */

export const startPolling = createAsyncThunk(
  "gaima/startPolling",
  async (
    args: {
      lesson: Lesson;
      learnerType: LearnerType;
      initialDocument: string;
      learnerCharacteristics: string[];
      modelName?: string;
      maxIterations?: number;
      pipelineType?: string;
      useRealStreaming?: boolean;
    },
    thunkAPI
  ): Promise<GaimaTask> => {
    const { lesson } = args;
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    if (lesson.type !== LessonType.TEXT_DOC || lesson.resources.length < 1) {
      throw new Error("invalid lesson");
    }
    return await fetchGaimaResult(
      args.initialDocument,
      args.learnerCharacteristics,
      args.modelName,
      args.maxIterations,
      args.pipelineType,
      args.useRealStreaming
    );
  }
);

export const poll = createAsyncThunk(
  "gaima/poll",
  async (id: string, thunkAPI): Promise<GaimaTask> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    const task = state.gaima.tasks.find((t) => t.id === id);
    if (!task) {
      throw new Error("No task started");
    }
    return await pollGaima(id);
  }
);

export const gaimaSlice = createSlice({
  name: "gaima",
  initialState: initialState,
  reducers: {
    modifyDocument: (
      state,
      action: PayloadAction<{ id: string; document: string }>
    ) => {
      const idx = state.tasks.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) {
        state.tasks[idx].result = {
          ...state.tasks[idx].result,
          modifiedDocument: action.payload.document,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPolling.pending, (state, action) => {
        const lesson = action.meta.arg.lesson.id;
        const learnerType = action.meta.arg.learnerType.id;
        const idx = state.tasks.findIndex(
          (t) => t.lesson === lesson && t.learnerType === learnerType
        );
        state.tasks = copyAndSet(state.tasks, idx, {
          id: "",
          status: GaimaStatus.QUEUED,
          statusUrl: "",
          result: {
            originalDocument: action.meta.arg.initialDocument,
            modifiedDocument: action.meta.arg.initialDocument,
            lineChanges: [],
          },
          error: "",
          lesson: lesson,
          learnerType: learnerType,
        });
      })
      .addCase(startPolling.fulfilled, (state, action) => {
        const lesson = action.meta.arg.lesson.id;
        const learnerType = action.meta.arg.learnerType.id;
        const idx = state.tasks.findIndex(
          (t) => t.lesson === lesson && t.learnerType === learnerType
        );
        if (idx !== -1) {
          state.tasks[idx].id = action.payload.id;
          state.tasks[idx].status = action.payload.status;
          state.tasks[idx].statusUrl = action.payload.statusUrl;
          if (action.payload.status === GaimaStatus.FAILED) {
            state.tasks[idx].error = "";
          } else {
            state.tasks[idx].error = `${action.payload.result}`;
          }
        }
      })
      .addCase(startPolling.rejected, (state, action) => {
        const lesson = action.meta.arg.lesson.id;
        const learnerType = action.meta.arg.learnerType.id;
        const idx = state.tasks.findIndex(
          (t) => t.lesson === lesson && t.learnerType === learnerType
        );
        if (idx !== -1) {
          state.tasks[idx].status = GaimaStatus.FAILED;
          state.tasks[idx].error = extractErrorMessageFromError(action.error);
        }
      })

      .addCase(poll.fulfilled, (state, action) => {
        const id = action.meta.arg;
        const idx = state.tasks.findIndex((t) => t.id === id);
        if (idx !== -1) {
          state.tasks[idx].id = action.payload.id;
          state.tasks[idx].status = action.payload.status;
          state.tasks[idx].statusUrl = action.payload.statusUrl;
          if (action.payload.status === GaimaStatus.FAILED) {
            state.tasks[idx].error = `${action.payload.result}`;
          } else {
            state.tasks[idx].error = "";
            const result = action.payload.result;
            if (result) {
              const originalDocument = state.tasks[idx].result.originalDocument;
              const modifiedLines = originalDocument.split("\n");
              for (const change of result.lineChanges) {
                modifiedLines[change.lineNumber - 1] = change.modified;
              }
              state.tasks[idx].result.lineChanges = result.lineChanges;
              state.tasks[idx].result.modifiedDocument =
                modifiedLines.join("\n");
            }
          }
        }
      })
      .addCase(poll.rejected, (state, action) => {
        const id = action.meta.arg;
        const idx = state.tasks.findIndex((t) => t.id === id);
        if (idx !== -1) {
          state.tasks[idx].status = GaimaStatus.FAILED;
          state.tasks[idx].error = extractErrorMessageFromError(action.error);
        }
      });
  },
});

export const { modifyDocument } = gaimaSlice.actions;

export default gaimaSlice.reducer;
